import {
  SparklesIcon,
  TwitterIcon,
  SlackIcon,
  YoutubeIcon,
  FacebookIcon,
  InstagramIcon,
  GithubIcon,
  LinkedinIcon,
  ChromeIcon,
  RssIcon,
  type LucideIcon
} from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

const avatars = [
  'https://cdn.shadcnstudio.com/ss-assets/avatar/avatar-45.png',
  'https://cdn.shadcnstudio.com/ss-assets/avatar/avatar-59.png',
  'https://cdn.shadcnstudio.com/ss-assets/avatar/avatar-34.png',
  'https://cdn.shadcnstudio.com/ss-assets/avatar/avatar-58.png'
]

const socialIcons: { name: string; icon: LucideIcon; color: string }[] = [
  { name: 'Twitter', icon: TwitterIcon, color: 'text-sky-500' },
  { name: 'YouTube', icon: YoutubeIcon, color: 'text-red-500' },
  { name: 'Slack', icon: SlackIcon, color: 'text-purple-500' },
  { name: 'Instagram', icon: InstagramIcon, color: 'text-pink-500' },
  { name: 'Facebook', icon: FacebookIcon, color: 'text-blue-600' },
  { name: 'Github', icon: GithubIcon, color: 'text-foreground' },
  { name: 'LinkedIn', icon: LinkedinIcon, color: 'text-blue-500' },
  { name: 'Chrome', icon: ChromeIcon, color: 'text-green-500' },
  { name: 'RSS', icon: RssIcon, color: 'text-orange-500' }
]

function AvatarGroup() {
  return (
    <div className='flex -space-x-2'>
      {avatars.map((src, i) => (
        <Avatar key={i} className='size-8 border-2 border-background'>
          <AvatarImage src={src} alt='User avatar' />
          <AvatarFallback>U</AvatarFallback>
        </Avatar>
      ))}
    </div>
  )
}

function SocialIconsGrid() {
  return (
    <div className='flex h-full items-center justify-center overflow-hidden'>
      <div className='-rotate-[9deg] grid grid-cols-3 gap-3 px-4'>
        {socialIcons.map((item) => {
          const Icon = item.icon
          return (
            <div
              key={item.name}
              className='flex size-14 items-center justify-center rounded-xl border bg-card shadow-sm'
            >
              <Icon className={`size-7 ${item.color}`} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

const FeaturesSection12 = () => {
  return (
    <section className='py-8 sm:py-16 lg:py-24'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <div className='mb-12 flex flex-col items-center gap-4 text-center sm:mb-16 lg:mb-24'>
          <h2 className='max-w-2xl text-2xl font-semibold md:text-3xl lg:text-4xl'>
            How ProspectHunter works
          </h2>
          <p className='text-muted-foreground max-w-xl text-xl'>
            ProspectHunter turns the entire internet into your real-time intelligence engine. Chat with AI agents to identify, verify and connect with the exact people who matter.
          </p>
          <div className='flex gap-4'>
            <Button
              className='rounded-xl text-base has-[>svg]:px-6'
              size='lg'
              onClick={() => {
                const input = document.getElementById('hero-website-input')
                if (input) {
                  input.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  setTimeout(() => input.focus(), 500)
                }
              }}
            >
              Get started
              <SparklesIcon className='size-4' />
            </Button>
            <Button variant='secondary' className='rounded-xl text-base' size='lg' asChild>
              <a href='#features'>Learn more</a>
            </Button>
          </div>
        </div>

        {/* Row 1 - 3 cards */}
        <div className='flex flex-col gap-6'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
            {/* Card: User Stats */}
            <Card className='overflow-hidden'>
              <CardContent className='flex flex-col gap-4 p-0'>
                <div className='bg-muted flex h-72 flex-col items-center justify-center gap-3 rounded-lg mx-6 mt-6'>
                  <p className='text-5xl font-semibold'>12K</p>
                  <p className='text-muted-foreground text-base'>happy users</p>
                  <AvatarGroup />
                </div>
                <div className='space-y-2 px-6 pb-6 text-center'>
                  <p className='text-lg font-semibold'>Chat</p>
                  <p className='text-muted-foreground text-base'>
                    The way to build an AI agent is as easy as talking to a real consultant.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card: Community / Profile */}
            <Card className='overflow-hidden'>
              <CardContent className='flex flex-col gap-4 p-0'>
                <div className='bg-muted flex h-72 flex-col items-center justify-center rounded-lg mx-6 mt-6'>
                  <div className='flex flex-col items-start gap-4 px-6'>
                    <AvatarGroup />
                    <div className='space-y-1'>
                      <p className='text-lg font-semibold'>A2 Designers</p>
                      <p className='text-muted-foreground text-base'>
                        Community of the best product designers.
                      </p>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      <Badge>Product</Badge>
                      <Badge>Design</Badge>
                      <Badge>UX</Badge>
                      <Badge>UI</Badge>
                    </div>
                  </div>
                </div>
                <div className='space-y-2 px-6 pb-6 text-center'>
                  <p className='text-lg font-semibold'>Search</p>
                  <p className='text-muted-foreground text-base'>
                    Conduct the bulk search you cannot achieve elsewhere, with human-grade quality.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card: Integrations */}
            <Card className='overflow-hidden'>
              <CardContent className='flex flex-col gap-4 p-0'>
                <div className='bg-muted flex h-72 items-center justify-center overflow-hidden rounded-lg mx-6 mt-6'>
                  <SocialIconsGrid />
                </div>
                <div className='space-y-2 px-6 pb-6 text-center'>
                  <p className='text-lg font-semibold'>Follow</p>
                  <p className='text-muted-foreground text-base'>
                    Never miss any live signals. From new hires, product releases, social media updates, to recent news.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 2 - 2 cards */}
          <div className='grid grid-cols-1 gap-6 md:grid-cols-[1fr_auto]'>
            {/* Card: AI Dashboard */}
            <Card className='overflow-hidden'>
              <CardContent className='flex flex-col gap-4 p-0'>
                <div className='bg-muted relative flex h-72 flex-col items-center justify-end overflow-hidden rounded-lg mx-6 mt-6'>
                  <div className='relative z-10 mx-auto w-[80%] overflow-hidden rounded-t-2xl border border-b-0 bg-card shadow-lg'>
                    <div className='flex items-center gap-2 border-b px-4 py-2'>
                      <div className='flex gap-1.5'>
                        <div className='size-2.5 rounded-full bg-red-400' />
                        <div className='size-2.5 rounded-full bg-yellow-400' />
                        <div className='size-2.5 rounded-full bg-green-400' />
                      </div>
                      <div className='text-muted-foreground flex-1 text-center text-xs'>
                        Real Estate Branding
                      </div>
                    </div>
                    <div className='space-y-3 p-4'>
                      <div className='flex items-center justify-between'>
                        <div className='flex gap-2'>
                          <Badge variant='outline' className='text-xs'>Objective</Badge>
                          <Badge variant='outline' className='text-xs'>Tasks</Badge>
                        </div>
                        <div className='flex -space-x-1'>
                          {avatars.slice(0, 3).map((src, i) => (
                            <Avatar key={i} className='size-5 border border-background'>
                              <AvatarImage src={src} />
                            </Avatar>
                          ))}
                        </div>
                      </div>
                      <div className='flex gap-2'>
                        <div className='h-1.5 flex-1 rounded-full bg-primary/20'>
                          <div className='h-full w-[70%] rounded-full bg-primary' />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='absolute bottom-0 left-0 right-0 h-16 bg-card' />
                  <div className='bg-muted absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border px-4 py-2'>
                    <span className='text-muted-foreground text-xs'>
                      Generate a cover letter for an AI company
                    </span>
                    <Button size='sm' className='h-6 gap-1 rounded-md px-2 text-xs'>
                      <SparklesIcon className='size-3' />
                      Generate
                    </Button>
                  </div>
                </div>
                <div className='space-y-2 px-6 pb-6 text-center'>
                  <p className='text-lg font-semibold'>Action</p>
                  <p className='text-muted-foreground text-base'>
                    Move beyond templates. ProspectHunter leverages deep contextual AI and multimodal intelligence to ensure every engagement message truly resonates.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card: Integration Ready */}
            <Card className='max-w-full overflow-hidden md:max-w-[390px]'>
              <CardContent className='flex flex-col gap-4 p-0'>
                <div className='bg-muted flex h-72 items-center justify-center overflow-hidden rounded-lg mx-6 mt-6'>
                  <div className='relative flex size-56 items-center justify-center'>
                    <div className='absolute size-56 rounded-full border border-border/40' />
                    <div className='absolute size-40 rounded-full border border-border/60' />
                    <div className='absolute size-28 rounded-full border border-border/80' />
                    <div className='bg-primary flex size-16 items-center justify-center rounded-2xl shadow-lg'>
                      <SparklesIcon className='size-8 text-primary-foreground' />
                    </div>
                  </div>
                </div>
                <div className='space-y-2 px-6 pb-6 text-center'>
                  <p className='text-lg font-semibold'>Active Agentic Signal</p>
                  <p className='text-muted-foreground text-base'>
                    Static profiles are obsolete. ProspectHunter tracks high-signal changes across the live web. Get alerted the moment a real opportunity appears.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  )
}

export default FeaturesSection12
